(() => {
  const elementName = 'product-360-viewer';

  if (customElements.get(elementName)) {
    return;
  }

  class Product360Viewer extends HTMLElement {
    constructor() {
      super();

      this.frames = [];
      this.framePromises = [];
      this.preloadedImages = [];
      this.currentIndex = 0;
      this.accumulatedX = 0;
      this.lastPointerX = 0;
      this.isDragging = false;
      this.dragInputType = null;
      this.hasStartedLoading = false;
      this.wasSwiperTouchAllowed = null;
      this.swiperInstance = null;
      this.slideObserver = null;

      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleKeyDown = this.handleKeyDown.bind(this);
      this.handlePointerEnter = this.handlePointerEnter.bind(this);
    }

    connectedCallback() {
      this.stage = this.querySelector('.product-360-viewer__stage');
      this.image = this.querySelector('.product-360-viewer__image');
      this.frames = this.parseFrames(this.dataset.frames);

      if (!this.stage || !this.frames.length) {
        this.classList.add('product-360-viewer--empty');
        return;
      }

      if (!this.image) {
        this.image = document.createElement('img');
        this.image.className = 'product-gallery__image product-360-viewer__image';
        this.image.alt = `${this.dataset.title || 'Product'} 360`;
        this.image.loading = 'lazy';
        this.image.decoding = 'async';
        this.stage.prepend(this.image);
      }

      if (!this.image.getAttribute('src')) {
        this.image.src = this.dataset.thumb || this.frames[0];
      }

      this.stage.addEventListener('pointerdown', this.handlePointerDown, true);
      this.stage.addEventListener('pointermove', this.handlePointerMove, true);
      this.stage.addEventListener('pointerup', this.handlePointerUp, true);
      this.stage.addEventListener('pointercancel', this.handlePointerUp, true);
      this.stage.addEventListener('touchstart', this.handleTouchStart, { capture: true, passive: false });
      this.stage.addEventListener('touchmove', this.handleTouchMove, { capture: true, passive: false });
      this.stage.addEventListener('touchend', this.handleTouchEnd, { capture: true, passive: false });
      this.stage.addEventListener('touchcancel', this.handleTouchEnd, { capture: true, passive: false });
      this.stage.addEventListener('keydown', this.handleKeyDown);

      if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
        this.stage.addEventListener('pointerenter', this.handlePointerEnter, { once: true });
      }

      this.observeActiveSlide();
    }

    disconnectedCallback() {
      if (!this.stage) {
        return;
      }

      this.stage.removeEventListener('pointerdown', this.handlePointerDown, true);
      this.stage.removeEventListener('pointermove', this.handlePointerMove, true);
      this.stage.removeEventListener('pointerup', this.handlePointerUp, true);
      this.stage.removeEventListener('pointercancel', this.handlePointerUp, true);
      this.stage.removeEventListener('touchstart', this.handleTouchStart, { capture: true });
      this.stage.removeEventListener('touchmove', this.handleTouchMove, { capture: true });
      this.stage.removeEventListener('touchend', this.handleTouchEnd, { capture: true });
      this.stage.removeEventListener('touchcancel', this.handleTouchEnd, { capture: true });
      this.stage.removeEventListener('keydown', this.handleKeyDown);
      this.stage.removeEventListener('pointerenter', this.handlePointerEnter);

      if (this.slideObserver) {
        this.slideObserver.disconnect();
      }
    }

    parseFrames(rawFrames) {
      if (!rawFrames) {
        return [];
      }

      let value = rawFrames;

      try {
        value = JSON.parse(value);
      } catch (error) {
        value = rawFrames;
      }

      if (typeof value === 'string') {
        const trimmedValue = value.trim();

        if (trimmedValue.startsWith('[')) {
          try {
            value = JSON.parse(trimmedValue);
          } catch (error) {
            value = trimmedValue;
          }
        }
      }

      if (Array.isArray(value)) {
        return value.map((frame) => this.extractFrameUrl(frame)).filter(Boolean);
      }

      if (value && typeof value === 'object') {
        return Object.values(value).map((frame) => this.extractFrameUrl(frame)).filter(Boolean);
      }

      if (typeof value !== 'string') {
        return [];
      }

      return value
        .split(/\r?\n|,|\|/)
        .map((frame) => frame.trim())
        .filter(Boolean);
    }

    extractFrameUrl(frame) {
      if (!frame) {
        return '';
      }

      if (typeof frame === 'string') {
        return frame.trim();
      }

      if (frame.url) {
        return String(frame.url).trim();
      }

      if (frame.src) {
        return String(frame.src).trim();
      }

      if (frame.preview_image && frame.preview_image.src) {
        return String(frame.preview_image.src).trim();
      }

      return '';
    }

    observeActiveSlide() {
      this.slideElement = this.closest('.swiper-slide');

      if (!this.slideElement) {
        return;
      }

      const loadWhenActive = () => {
        if (this.slideElement.classList.contains('swiper-slide-active')) {
          this.loadFrames();
        }
      };

      this.slideObserver = new MutationObserver(loadWhenActive);
      this.slideObserver.observe(this.slideElement, {
        attributes: true,
        attributeFilter: ['class']
      });

      loadWhenActive();
    }

    handlePointerEnter() {
      this.loadFrames();
    }

    handlePointerDown(event) {
      if (event.target.closest('[data-product-gallery-item]')) {
        return;
      }

      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      if (this.isDragging && this.dragInputType !== 'pointer') {
        this.blockPointerEvent(event);
        return;
      }

      this.loadFrames();
      this.disableSwiperTouch();

      this.isDragging = true;
      this.dragInputType = 'pointer';
      this.lastPointerX = event.clientX;
      this.accumulatedX = 0;
      this.classList.add('product-360-viewer--dragging');

      if (this.stage.setPointerCapture) {
        this.stage.setPointerCapture(event.pointerId);
      }

      this.blockPointerEvent(event);
    }

    handlePointerMove(event) {
      if (!this.isDragging) {
        return;
      }

      if (this.dragInputType !== 'pointer') {
        this.blockPointerEvent(event);
        return;
      }

      this.rotateToClientX(event.clientX);

      this.blockPointerEvent(event);
    }

    handlePointerUp(event) {
      if (!this.isDragging) {
        return;
      }

      if (this.dragInputType !== 'pointer') {
        this.blockPointerEvent(event);
        return;
      }

      this.isDragging = false;
      this.dragInputType = null;
      this.classList.remove('product-360-viewer--dragging');
      this.enableSwiperTouch();

      if (
        this.stage.releasePointerCapture &&
        (!this.stage.hasPointerCapture || this.stage.hasPointerCapture(event.pointerId))
      ) {
        this.stage.releasePointerCapture(event.pointerId);
      }

      this.blockPointerEvent(event);
    }

    handleTouchStart(event) {
      if (event.target.closest('[data-product-gallery-item]')) {
        return;
      }

      const touch = this.getTouch(event);

      if (!touch) {
        return;
      }

      if (this.isDragging && this.dragInputType !== 'touch') {
        this.blockTouchEvent(event);
        return;
      }

      this.loadFrames();
      this.disableSwiperTouch();

      this.isDragging = true;
      this.dragInputType = 'touch';
      this.lastPointerX = touch.clientX;
      this.accumulatedX = 0;
      this.classList.add('product-360-viewer--dragging');
      this.blockTouchEvent(event);
    }

    handleTouchMove(event) {
      if (!this.isDragging) {
        return;
      }

      if (this.dragInputType !== 'touch') {
        this.blockTouchEvent(event);
        return;
      }

      const touch = this.getTouch(event);

      if (!touch) {
        return;
      }

      this.rotateToClientX(touch.clientX);
      this.blockTouchEvent(event);
    }

    handleTouchEnd(event) {
      if (!this.isDragging) {
        return;
      }

      if (this.dragInputType !== 'touch') {
        this.blockTouchEvent(event);
        return;
      }

      this.isDragging = false;
      this.dragInputType = null;
      this.classList.remove('product-360-viewer--dragging');
      this.enableSwiperTouch();
      this.blockTouchEvent(event);
    }

    getTouch(event) {
      const touchList = event.touches && event.touches.length ? event.touches : event.changedTouches;
      return touchList && touchList.length ? touchList[0] : null;
    }

    rotateToClientX(clientX) {
      const deltaX = clientX - this.lastPointerX;
      const pixelsPerFrame = Math.max(4, Number(this.dataset.pixelsPerFrame) || 12);

      this.lastPointerX = clientX;
      this.accumulatedX += deltaX;

      while (Math.abs(this.accumulatedX) >= pixelsPerFrame) {
        const direction = this.accumulatedX > 0 ? -1 : 1;
        this.setFrame(this.currentIndex + direction);
        this.accumulatedX += this.accumulatedX > 0 ? -pixelsPerFrame : pixelsPerFrame;
      }
    }

    blockPointerEvent(event) {
      if (event.cancelable) {
        event.preventDefault();
      }

      event.stopPropagation();

      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
    }

    blockTouchEvent(event) {
      if (event.cancelable) {
        event.preventDefault();
      }

      event.stopPropagation();

      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
    }

    handleKeyDown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      this.loadFrames();
      this.setFrame(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
      event.preventDefault();
    }

    loadFrames() {
      if (this.hasStartedLoading || !this.frames.length) {
        return;
      }

      this.hasStartedLoading = true;
      this.classList.add('product-360-viewer--loading');

      this.preloadFrame(this.currentIndex).then(() => {
        this.setFrame(this.currentIndex);
        this.classList.remove('product-360-viewer--loading');
        this.classList.add('product-360-viewer--loaded');
      });

      [1, -1, 2, -2].forEach((offset) => {
        this.preloadFrame(this.currentIndex + offset);
      });

      this.runWhenIdle(() => {
        this.frames.forEach((frame, index) => {
          this.preloadFrame(index);
        });
      });
    }

    preloadFrame(index) {
      const normalizedIndex = this.normalizeIndex(index);

      if (this.framePromises[normalizedIndex]) {
        return this.framePromises[normalizedIndex];
      }

      this.framePromises[normalizedIndex] = new Promise((resolve) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = () => resolve(image);
        image.decoding = 'async';
        image.src = this.frames[normalizedIndex];

        this.preloadedImages[normalizedIndex] = image;
      });

      return this.framePromises[normalizedIndex];
    }

    setFrame(index) {
      const normalizedIndex = this.normalizeIndex(index);
      const nextSrc = this.frames[normalizedIndex];

      if (!nextSrc) {
        return;
      }

      this.currentIndex = normalizedIndex;

      if (this.image.getAttribute('src') !== nextSrc) {
        this.image.src = nextSrc;
      }
    }

    normalizeIndex(index) {
      const frameCount = this.frames.length;
      let nextIndex = index % frameCount;

      if (nextIndex < 0) {
        nextIndex += frameCount;
      }

      return nextIndex;
    }

    runWhenIdle(callback) {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(callback, { timeout: 1500 });
        return;
      }

      window.setTimeout(callback, 300);
    }

    disableSwiperTouch() {
      const swiperElement = this.closest('.swiper');
      this.swiperInstance = swiperElement && swiperElement.swiper ? swiperElement.swiper : null;

      if (!this.swiperInstance) {
        return;
      }

      this.wasSwiperTouchAllowed = this.swiperInstance.allowTouchMove;
      this.swiperInstance.allowTouchMove = false;
    }

    enableSwiperTouch() {
      if (!this.swiperInstance) {
        return;
      }

      this.swiperInstance.allowTouchMove = this.wasSwiperTouchAllowed !== false;
      this.swiperInstance = null;
      this.wasSwiperTouchAllowed = null;
    }
  }

  customElements.define(elementName, Product360Viewer);
})();
